// src/App.js
import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import "./App.css";
import { useAuthData } from "./hooks/useAuthData";
import { useUserEndpoints } from "./hooks/useUserEndpoints";
import { useConfig } from "./hooks/useConfig";
import { acceptDsa, rejectDsa, fetchData, getUserTokenFromBackend } from "./services/api";
import { getAcceptedDsaDocument } from "./services/dsaDocuments";
import { transformConfigToEndpoints } from "./utils/constants";
import Header from "./components/Header";
import Sidebar from "./components/Sidebar";
import MarkdownDoc from "./components/MarkdownDoc";
import DataRequestForm from "./components/DataRequestForm";
import AuthErrorDisplay from "./components/AuthErrorDisplay";
import AccessKeyManager from "./components/AccessKeyManager";
import {
  AccessDenied,
  DsaSuccess,
  Loading,
  LoginPrompt,
  EmailNotVerified,
  UserInactive
} from "./components/AuthScreens";
import UserProfileCard from "./components/UserProfileCard";
import MetadataBrowser from "./components/MetadataBrowser";
import AdminUsersScreen from "./components/AdminUsersScreen";

function DsaGateLoader(props) {
  const [Comp, setComp] = useState(null);

  useEffect(() => {
    let mounted = true;
    (async () => {
      const mod = await import("./components/DsaGate");
      if (mounted) setComp(() => mod.default);
    })();
    return () => {
      mounted = false;
    };
  }, []);

  if (!Comp) return <Loading />;
  return <Comp {...props} />;
}

const deriveDatasetsFromConfig = (config) => {
  return (config?.datasets || []).map((dataset) => ({
    id: dataset.name,
    label:
      dataset.displayName ||
      dataset.name.charAt(0).toUpperCase() +
        dataset.name.slice(1),
    documentationId: dataset.name,
    _raw: dataset,
  }));
};

const computeAccessibleDatasetIdsFromEndpoints = (config, userEndpoints) => {
  const accessible = new Set();
  if (!config?.datasets || !Array.isArray(userEndpoints)) return accessible;

  for (const ds of config.datasets) {
    const endpointItems = (ds.endpoints || []).flatMap((ep) => ep.items || []);
    const hasAny = endpointItems.some((item) => {
      const endpointCode = item.endpointCode || item.displayName;
      return endpointCode && userEndpoints.includes(endpointCode);
    });
    if (hasAny) accessible.add(ds.name);
  }
  return accessible;
};

function Toast({ message, type, onClose }) {
  useEffect(() => {
    const timer = setTimeout(() => {
      onClose();
    }, 4000);
    return () => clearTimeout(timer);
  }, [onClose]);

  return (
    <div className={`toast toast-${type}`}>
      <span>{message}</span>
      <button className="toast-close" onClick={onClose} aria-label="Close notification">
        ×
      </button>
    </div>
  );
}

const parseAcceptedAt = (s) => {
  if (!s || typeof s !== "string") return null;
  const m = s.match(/^(\d{4}-\d{2}-\d{2})\s+(\d{2}:\d{2}:\d{2})\s+UTC$/);
  if (m) return new Date(`${m[1]}T${m[2]}Z`);
  const d = new Date(s);
  return isNaN(d.getTime()) ? null : d;
};

function App() {
  const {
    isLoading: isAuth0Loading,
    isAuthenticated,
    error: auth0Error,
    getAccessTokenSilently,
    loginWithRedirect,
    logout,
  } = useAuth0();

  const {
    user,
    userPermissions,
    isValidated,
    isDsaAccepted,
    dsaInfo,
    isEmailVerified,
    loading: isAuthDataLoading,
    getNamespacedClaim: getNamespacedClaimHook,
    userInfoReady,
    refreshDsaStatus,
  } = useAuthData();

  const permsLoaded = userInfoReady && (Array.isArray(userPermissions) || userPermissions === null);
  const isAdmin = Array.isArray(userPermissions) && userPermissions.includes("access:admin");

  const lookupEnabled =
    isAuthenticated &&
    !isAuth0Loading &&
    permsLoaded &&
    isAdmin === false;

  const {
    endpoints: userEndpoints,
    userInfo: csvUserInfo,
    loading: endpointsLoading,
    error: endpointsError,
    isActive: isUserActive,
    isInactive: isUserInactive,
  } = useUserEndpoints(
    user?.email,
    lookupEnabled,
    isAdmin
  );

  const [isRequestInProgress, setIsRequestInProgress] = useState(false);
  const [isDsaActionInProgress, setIsDsaActionInProgress] = useState(false);
  const [dsaAcceptanceSuccess, setDsaAcceptanceSuccess] = useState(false);

  const [responseMessage, setResponseMessage] = useState("");
  const [isErrorResponseMessage, setIsErrorResponseMessage] = useState(false);
  const [noDataInfoMessage, setNoDataInfoMessage] = useState("");

  const [activeTab, setActiveTab] = useState("main");
  const [selectedDatasetId, setSelectedDatasetId] = useState(null);

  const { config, loading: configLoading, error: configError } = useConfig();

  const allPossibleEndpoints = useMemo(() => {
    return transformConfigToEndpoints(config);
  }, [config]);

  const allEndpointsLoading = configLoading;
  const allEndpointsError = configError;

  const [dsaAcceptedLocal, setDsaAcceptedLocal] = useState(null);

  const [fixedOrg, setFixedOrg] = useState(null);

  const [toasts, setToasts] = useState([]);

  const effectiveDsaAccepted = dsaAcceptedLocal ?? isDsaAccepted;

  const dsaLoaded = typeof isDsaAccepted === "boolean";
  const emailVerifiedLoaded = typeof isEmailVerified === "boolean";
  const validatedLoaded = typeof isValidated === "boolean";

  const derivedDatasets = useMemo(() => deriveDatasetsFromConfig(config || { datasets: [] }), [config]);

  const accessibleDatasetIds = useMemo(
    () => computeAccessibleDatasetIdsFromEndpoints(config, userEndpoints),
    [config, userEndpoints]
  );

  const visibleDatasets = useMemo(() => {
    if (!accessibleDatasetIds || accessibleDatasetIds.size === 0) return [];
    return derivedDatasets.filter((ds) => accessibleDatasetIds.has(ds.id));
  }, [derivedDatasets, accessibleDatasetIds]);

  const organization = useMemo(() => {
    return (
      csvUserInfo?.requestorOrgName ||
      getNamespacedClaimHook(user, "organization") ||
      user?.organization ||
      "default"
    );
  }, [csvUserInfo, user, getNamespacedClaimHook]);

  const userName = useMemo(() => {
    return `${csvUserInfo?.firstName || user?.given_name || ""} ${csvUserInfo?.lastName || user?.family_name || ""}`.trim() || "Unknown User";
  }, [csvUserInfo, user]);

  const userRole = useMemo(() => {
    return (
      csvUserInfo?.roleDesignation ||
      getNamespacedClaimHook(user, "role_designation") ||
      user?.role_designation ||
      "Unknown Role"
    );
  }, [csvUserInfo, user, getNamespacedClaimHook]);

  const [userToken, setUserToken] = useState(null);
  const [tokenExpiry, setTokenExpiry] = useState(null);

  const formattedDsaDate = useMemo(() => {
    const d = parseAcceptedAt(dsaInfo?.accepted_at);
    if (!d) return null;
    return d.toISOString().split("T")[0];
  }, [dsaInfo]);

  const creationDate = useMemo(() => {
    if (!user?.created_at) return null;
    return new Date(user.created_at).toISOString().split("T")[0];
  }, [user]);

  const handleDownloadDsa = useCallback(async () => {
    try {
      await getAcceptedDsaDocument({
        getAccessTokenSilently,
        format: "pdf",
        download: true,
      });
    } catch (error) {
      console.error("Error downloading DSA:", error);
      alert(
        "Unable to download DSA document. " +
        "It might still be processing or not yet available."
      );
    }
  }, [getAccessTokenSilently]);

  const addToast = useCallback((message, type = "success") => {
    const id = Date.now();
    setToasts(prev => [...prev, { id, message, type }]);
  }, []);

  const removeToast = useCallback((id) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  }, []);

  const handleLoadToken = useCallback(async () => {
    try {
      const data = await getUserTokenFromBackend(getAccessTokenSilently);
      const rawToken = data.token;

      const payload = JSON.parse(atob(rawToken.split(".")[1]));
      setUserToken(rawToken);
      setTokenExpiry(new Date(payload.exp * 1000));
    } catch (err) {
      console.error("Error loading token:", err);
    }
  }, [getAccessTokenSilently]);

  useEffect(() => {
    if (activeTab === "access-key" && !userToken) {
      handleLoadToken();
    }
  }, [activeTab, handleLoadToken, userToken]);

  useEffect(() => {
    if (activeTab === "metadata") {
      const stillVisible = visibleDatasets.find((d) => d.id === selectedDatasetId);
      if (!selectedDatasetId || !stillVisible) {
        setSelectedDatasetId(visibleDatasets[0]?.id || null);
      }
    }
  }, [activeTab, visibleDatasets, selectedDatasetId]);

  const handleAcceptDsa = useCallback(async () => {
    try {
      setIsDsaActionInProgress(true);
      await acceptDsa({
        getAccessTokenSilently,
        userEmail: user?.email,
        userName: userName,
        userRole: userRole
      });

      await refreshDsaStatus();

      setDsaAcceptanceSuccess(true);
      setDsaAcceptedLocal(true);

      addToast("DSA accepted successfully", "success");
    } catch (err) {
      console.error(err);
      alert(err.message);
    } finally {
      setIsDsaActionInProgress(false);
    }
  }, [getAccessTokenSilently, user, userName, userRole, refreshDsaStatus, addToast]);

  const handleRejectDsa = async () => {
    if (!isAuthenticated) return;
    try {
      setIsDsaActionInProgress(true);
      await rejectDsa({
        getAccessTokenSilently,
        userEmail: user?.email
      });

      addToast("DSA rejected", "info");

      setTimeout(() => {
        logout({ logoutParams: { returnTo: window.location.origin } });
      }, 1500);
    } catch (err) {
      console.error(err);
      alert(err.message);
      setIsDsaActionInProgress(false);
    }
  };

  const handleSubmit = async ({ selectedEndpoint, dataFormat, selectedYear, getDynamicFilename }) => {
    if (!isAuthenticated || !effectiveDsaAccepted || !isValidated || !isEmailVerified) {
      setResponseMessage("You do not have permission to perform this action.");
      setIsErrorResponseMessage(true);
      return;
    }
    if (!selectedEndpoint) {
      setResponseMessage("No valid endpoint selected for the current year.");
      setIsErrorResponseMessage(true);
      return;
    }

    setResponseMessage("");
    setIsErrorResponseMessage(false);
    setNoDataInfoMessage("");
    setIsRequestInProgress(true);

    try {
      const result = await fetchData({
        getAccessTokenSilently,
        selectedEndpoint,
        dataFormat,
        selectedYear,
        getDynamicFilename,
      });

      if (result?.noData) {
        setNoDataInfoMessage("No data available to download for the selected criteria.");
      } else if (result?.success) {
        setResponseMessage("Request successful.");
      } else {
        setResponseMessage("Request completed.");
      }
    } catch (error) {
      console.error("API request error:", error);
      setResponseMessage(`Error: ${error.message}`);
      setIsErrorResponseMessage(true);
    } finally {
      setIsRequestInProgress(false);
    }
  };

  const handleLogout = () => logout({ logoutParams: { returnTo: window.location.origin } });

  const gatingNotReady =
    isAuth0Loading ||
    allEndpointsLoading ||
    configLoading ||
    !userInfoReady ||
    !permsLoaded ||
    (isAuthenticated && isAuthDataLoading) ||
    (isAuthenticated && !isAdmin && endpointsLoading);

  const allAuthStatesReady =
    isAuthenticated &&
    !isAdmin &&
    dsaLoaded &&
    emailVerifiedLoaded &&
    validatedLoaded &&
    !endpointsLoading &&
    typeof effectiveDsaAccepted === "boolean" &&
    isUserActive !== null;

  const shouldShowDsaGate =
    allAuthStatesReady &&
    isEmailVerified === true &&
    isUserActive === true &&
    effectiveDsaAccepted === false;

  useEffect(() => {
    if (shouldShowDsaGate && organization && !fixedOrg) {
      setFixedOrg(organization);
    }
    if (!shouldShowDsaGate) {
      setFixedOrg(null);
    }
  }, [shouldShowDsaGate, organization, fixedOrg]);

  const hasAnyError = !!(auth0Error || allEndpointsError || configError || endpointsError);

  const renderContent = () => {
    if (gatingNotReady) {
      return <Loading />;
    }

    if (auth0Error) {
      return <AuthErrorDisplay error={auth0Error} onLogout={handleLogout} />;
    }

    if (allEndpointsError) {
      return <AuthErrorDisplay error={allEndpointsError} onLogout={handleLogout} />;
    }

    if (configError) {
      return <AuthErrorDisplay error={configError} onLogout={handleLogout} />;
    }

    if (endpointsError) {
      return <AuthErrorDisplay error={endpointsError} onLogout={handleLogout} />;
    }

    if (!isAuthenticated) {
      return (
        <LoginPrompt
          onLogin={() => loginWithRedirect({ authorizationParams: { prompt: "login" } })}
          onSignUp={() => loginWithRedirect({ authorizationParams: { screen_hint: "signup", prompt: "login" } })}
        />
      );
    }

    if (isAdmin) {
      if (isEmailVerified === false) return <EmailNotVerified />;
      if (!userInfoReady) return <Loading />;
      return <AdminUsersScreen getAccessTokenSilently={getAccessTokenSilently} />;
    }

    if (!allAuthStatesReady) {
      return <Loading />;
    }

    if (isEmailVerified === false) {
      return <EmailNotVerified />;
    }

    if (isUserInactive) {
      return <UserInactive />;
    }

    if (shouldShowDsaGate) {
      return dsaAcceptanceSuccess ? (
        <DsaSuccess />
      ) : (
        <DsaGateLoader
          isAccepted={false}
          onAccept={handleAcceptDsa}
          onReject={handleRejectDsa}
          isSubmitting={isDsaActionInProgress}
          organization={fixedOrg || organization}
          userName={userName}
          userRole={userRole}
          userEmail={user?.email}
          getAccessTokenSilently={getAccessTokenSilently}
        />
      );
    }

    if (isValidated === false) {
      return (
        <AccessDenied
          userName={userName}
          userRole={userRole}
          organization={organization}
          getAccessTokenSilently={getAccessTokenSilently}
          userEmail={user?.email}
        />
      );
    }

    return (
      <>
        {activeTab === "main" && (
          <div className="content-container">
            <DataRequestForm
              allPossibleEndpoints={allPossibleEndpoints}
              isRequestInProgress={isRequestInProgress}
              onSubmit={handleSubmit}
              responseMessage={responseMessage}
              isErrorResponseMessage={isErrorResponseMessage}
              noDataInfoMessage={noDataInfoMessage}
              userEndpoints={userEndpoints}
            />
          </div>
        )}

        {/* === Access Key section === */}
        {activeTab === "access-key" && (
          <div className="content-container access-key-page">
            {/* PAT Manager Component */}
            <AccessKeyManager
              getAccessTokenSilently={getAccessTokenSilently}
              addToast={addToast}
            />

            <hr style={{ margin: "40px 0", border: "0", borderTop: "1px solid #eee" }} />

            {/* Auth0 Session Token */}
            <h3>Your Auth0 Token </h3>
            <div className="token-card">
              <div className="token-row">
                <code className="token-preview">
                  {userToken ? `${userToken.substring(0, 15)}...` : "Loading..."}
                </code>
                <button
                  className="copy-token-btn"
                  onClick={async () => {
                    if (!userToken) return;
                    try {
                      await navigator.clipboard.writeText(userToken);
                      addToast("Session token copied!", "success");
                    } catch (err) {
                      console.warn("Failed to copy token:", err);
                    }
                  }}
                  disabled={!userToken}
                >
                  Copy
                </button>
              </div>

              {userToken && (
                <div className="token-info-container" style={{ marginTop: "12px" }}>
                  <div className="token-info-row">
                    <strong>Expires on:</strong>
                    <span style={{ marginLeft: "8px" }}>{tokenExpiry?.toLocaleString()}</span>
                  </div>
                </div>
              )}

              {tokenExpiry && tokenExpiry < new Date() && (
                <p className="warning-text" style={{ color: "#ef4444", marginTop: "8px" }}>
                  Status: Expired
                </p>
              )}
            </div>

            {/* App Code section */}
            <h3>Your App Code</h3>

            <div className="token-card">
              <div className="token-row">
                <code className="token-preview">
                  {process.env.REACT_APP_FUNCTION_KEY
                    ? `${process.env.REACT_APP_FUNCTION_KEY.substring(0, 10)}...`
                    : "Not available"}
                </code>
                <button
                  className="copy-token-btn"
                  onClick={async () => {
                    const appCode = process.env.REACT_APP_FUNCTION_KEY;
                    if (!appCode) return;
                    try {
                      await navigator.clipboard.writeText(appCode);
                      addToast("App code copied!", "success");
                    } catch (err) {
                      console.warn("Failed to copy app code:", err);
                    }
                  }}
                  disabled={!process.env.REACT_APP_FUNCTION_KEY}
                >
                  Copy
                </button>
              </div>
            </div>
          </div>
        )}

        {activeTab === "how-to-use" && <MarkdownDoc path="/docs/how-to-use.md" />}
        {activeTab === "metadata" && (
          <MetadataBrowser
            datasets={visibleDatasets}
            selectedDatasetId={selectedDatasetId}
            setSelectedDatasetId={setSelectedDatasetId}
            getAccessTokenSilently={getAccessTokenSilently}
          />
        )}
      </>
    );
  };

  const isFullyAuthenticated =
    isAuthenticated &&
    effectiveDsaAccepted === true &&
    isValidated === true &&
    isEmailVerified === true &&
    isUserActive === true;

  return (
    <div className="App">
      <div className="toast-container">
        {toasts.map(toast => (
          <Toast
            key={toast.id}
            message={toast.message}
            type={toast.type}
            onClose={() => removeToast(toast.id)}
          />
        ))}
      </div>

      <Header
        isAuthenticated={isAuthenticated}
        showLogout={isAuthenticated || hasAnyError}
        onLogout={handleLogout}
      />
      <div
        className={`app-layout ${!isFullyAuthenticated ? "single-column" : ""} ${
          isAdmin ? "admin-layout" : ""
        }`}
      >
        {!isAdmin && isFullyAuthenticated && (
          <>
            <Sidebar
              activeTab={activeTab}
              setActiveTab={(tab) => {
                setActiveTab(tab);
                if (tab === "metadata" && !selectedDatasetId && visibleDatasets.length > 0) {
                  setSelectedDatasetId(visibleDatasets[0].id);
                }
              }}
            />
            <UserProfileCard
              firstName={csvUserInfo?.firstName || getNamespacedClaimHook(user, "first_name") || user?.first_name || user?.given_name}
              lastName={csvUserInfo?.lastName || getNamespacedClaimHook(user, "last_name") || user?.last_name || user?.family_name}
              email={user?.email}
              reasonForAccess={getNamespacedClaimHook(user, "reason_for_access") || user?.reason_for_access}
              roleDesignation={csvUserInfo?.roleDesignation || getNamespacedClaimHook(user, "role_designation") || user?.role_designation}
              organization={organization}
              creationDate={creationDate}
              dsaAcceptedDate={formattedDsaDate}
              onDownloadDsa={handleDownloadDsa}
            />
          </>
        )}
        <main className="main-content" tabIndex={-1}>
          {renderContent()}
        </main>
      </div>
    </div>
  );
}

export default App;